import { useCallback, useState } from 'react';
import { apiJson } from '../api/client.js';
import { FormSelect } from './FormControls.jsx';
import { clientLabel } from '../utils/clientLabel.js';

const ACTION_OPTIONS = [
  { value: 'create', label: 'Create new client' },
  { value: 'link', label: 'Link to existing client' },
  { value: 'linked', label: 'Already linked (keep)' },
  { value: 'skip', label: 'Skip' },
];

const OPP_ACTION_OPTIONS = [
  { value: 'create', label: 'Create new project' },
  { value: 'link', label: 'Link to existing project' },
  { value: 'linked', label: 'Already linked (keep)' },
  { value: 'skip', label: 'Skip' },
];

function confidenceBadge(confidence) {
  if (confidence === 'exact' || confidence === 'linked') {
    return <span className="badge badge-ok">auto</span>;
  }
  if (confidence === 'fuzzy') {
    return <span className="badge badge-warn">fuzzy</span>;
  }
  return <span className="badge text-bg-secondary">manual</span>;
}

export function GhlImportPanel({ ghlConfigured, onComplete }) {
  const [preview, setPreview] = useState(null);
  const [locationRows, setLocationRows] = useState([]);
  const [oppRows, setOppRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState('');
  const [result, setResult] = useState(null);

  const loadPreview = useCallback(async () => {
    setLoading(true);
    setError('');
    setResult(null);
    try {
      const data = await apiJson('/api/admin/ghl/import/preview', { method: 'POST', body: {} });
      setPreview(data);

      setLocationRows(
        (data.locations ?? []).map((loc) => ({
          ...loc,
          action: loc.suggested_action === 'linked' ? 'linked' : loc.suggested_action === 'link' ? 'link' : 'create',
          client_id: loc.matched_client_id ? String(loc.matched_client_id) : '',
          pipeline_id: loc.pipeline_id ?? '',
          pipeline_stage_id: loc.pipeline_stage_id ?? '',
          quickbooks_id: loc.quickbooks_customer_id ?? '',
        })),
      );

      setOppRows(
        (data.opportunities ?? []).map((opp) => ({
          ...opp,
          action: opp.suggested_action === 'linked' ? 'linked' : opp.suggested_action === 'link' ? 'link' : 'create',
          client_id: opp.matched_client_id ? String(opp.matched_client_id) : '',
          project_id: opp.matched_project_id ? String(opp.matched_project_id) : '',
        })),
      );
    } catch (e) {
      setError(e.message);
      setPreview(null);
    } finally {
      setLoading(false);
    }
  }, []);

  const portalClients = preview?.portal_clients ?? [];
  const portalProjects = preview?.portal_projects ?? [];
  const qbCustomers = preview?.quickbooks_customers ?? [];

  const updateLocation = (index, patch) => {
    setLocationRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const updateOpp = (index, patch) => {
    setOppRows((rows) => rows.map((r, i) => (i === index ? { ...r, ...patch } : r)));
  };

  const runImport = async () => {
    setRunning(true);
    setError('');
    setResult(null);
    try {
      const body = {
        locations: locationRows.map((r) => ({
          ghl_location_id: r.ghl_location_id,
          ghl_location_name: r.ghl_location_name,
          action: r.action,
          client_id: r.action === 'link' && r.client_id ? Number(r.client_id) : r.matched_client_id ?? undefined,
          pipeline_id: r.pipeline_id || null,
          pipeline_stage_id: r.pipeline_stage_id || null,
          ghl_sync_enabled: true,
        })),
        opportunities: oppRows.map((r) => ({
          ghl_opportunity_id: r.ghl_opportunity_id,
          ghl_location_id: r.ghl_location_id,
          name: r.name,
          ghl_status: r.ghl_status,
          action: r.action,
          client_id: r.client_id ? Number(r.client_id) : r.matched_client_id ?? undefined,
          project_id: r.action === 'link' && r.project_id ? Number(r.project_id) : r.matched_project_id ?? undefined,
        })),
        quickbooks: preview?.quickbooks_connected
          ? locationRows
              .filter((r) => r.quickbooks_id && r.action !== 'skip')
              .map((r) => ({
                client_id: r.client_id ? Number(r.client_id) : r.matched_client_id ?? undefined,
                ghl_location_id: r.ghl_location_id,
                quickbooks_id: r.quickbooks_id,
              }))
          : [],
      };

      const res = await apiJson('/api/admin/ghl/import/run', { method: 'POST', body });
      setResult(res.stats ?? res);
      onComplete?.();
    } catch (e) {
      setError(e.message);
    } finally {
      setRunning(false);
    }
  };

  if (!ghlConfigured) {
    return null;
  }

  return (
    <div className="auth-card flat" style={{ marginBottom: '1.5rem' }}>
      <h3 style={{ marginTop: 0 }}>Import from Go High Level</h3>
      <p className="muted small">
        Pull GHL locations as portal clients, opportunities as projects, and match QuickBooks customers by name.
        Review matches below — change anything that looks wrong before importing.
      </p>

      <div className="row-actions flex-wrap" style={{ marginBottom: '1rem' }}>
        <button type="button" className="btn btn-primary" disabled={loading} onClick={() => void loadPreview()}>
          {loading ? 'Loading preview…' : preview ? 'Refresh preview' : 'Preview import'}
        </button>
        {preview ? (
          <button type="button" className="btn btn-secondary" disabled={running || loading} onClick={() => void runImport()}>
            {running ? 'Importing…' : 'Run import'}
          </button>
        ) : null}
      </div>

      {error ? <p className="err">{error}</p> : null}
      {result ? (
        <pre className="sync-pre" style={{ marginBottom: '1rem' }}>
          {JSON.stringify(result, null, 2)}
        </pre>
      ) : null}

      {preview ? (
        <>
          <h4>Clients (GHL locations)</h4>
          <div className="table-responsive" style={{ marginBottom: '1.5rem' }}>
            <table className="table table-sm align-middle">
              <thead>
                <tr>
                  <th>GHL location</th>
                  <th>Match</th>
                  <th>Action</th>
                  <th>Portal client</th>
                  <th>Pipeline</th>
                  {preview.quickbooks_connected ? <th>QuickBooks</th> : null}
                </tr>
              </thead>
              <tbody>
                {locationRows.map((row, i) => {
                  const pipelines = row.pipelines ?? [];
                  const stages = pipelines.find((p) => p.id === row.pipeline_id)?.stages ?? [];
                  return (
                    <tr key={row.ghl_location_id}>
                      <td>
                        <strong>{row.ghl_location_name}</strong>
                        <div className="muted small">{confidenceBadge(row.match_confidence)}</div>
                      </td>
                      <td className="muted small">
                        {row.matched_client_name ?? '—'}
                      </td>
                      <td>
                        <FormSelect value={row.action} onChange={(e) => updateLocation(i, { action: e.target.value })}>
                          {ACTION_OPTIONS.map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </FormSelect>
                      </td>
                      <td>
                        <FormSelect
                          value={row.client_id}
                          disabled={row.action !== 'link'}
                          onChange={(e) => updateLocation(i, { client_id: e.target.value })}
                        >
                          <option value="">Select client…</option>
                          {portalClients.map((c) => (
                            <option key={c.id} value={c.id}>{clientLabel(c)}</option>
                          ))}
                        </FormSelect>
                      </td>
                      <td>
                        <FormSelect
                          value={row.pipeline_id}
                          onChange={(e) => updateLocation(i, { pipeline_id: e.target.value, pipeline_stage_id: '' })}
                        >
                          <option value="">Pipeline…</option>
                          {pipelines.map((p) => (
                            <option key={p.id} value={p.id}>{p.name}</option>
                          ))}
                        </FormSelect>
                        <FormSelect
                          value={row.pipeline_stage_id}
                          disabled={!row.pipeline_id}
                          onChange={(e) => updateLocation(i, { pipeline_stage_id: e.target.value })}
                          style={{ marginTop: '0.35rem' }}
                        >
                          <option value="">Stage (optional)</option>
                          {stages.map((s) => (
                            <option key={s.id} value={s.id}>{s.name}</option>
                          ))}
                        </FormSelect>
                      </td>
                      {preview.quickbooks_connected ? (
                        <td>
                          <FormSelect
                            value={row.quickbooks_id ?? ''}
                            onChange={(e) => updateLocation(i, { quickbooks_id: e.target.value })}
                          >
                            <option value="">None</option>
                            {(row.quickbooks_id &&
                            !qbCustomers.some((c) => c.id === row.quickbooks_id)
                              ? [{ id: row.quickbooks_id, name: row.quickbooks_customer_name ?? row.quickbooks_id }]
                              : []
                            ).concat(qbCustomers).map((c) => (
                              <option key={c.id} value={c.id}>{c.name}</option>
                            ))}
                          </FormSelect>
                          <div className="muted small">{confidenceBadge(row.quickbooks_match_confidence ?? 'none')}</div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {oppRows.length > 0 ? (
            <>
              <h4>Projects (GHL opportunities)</h4>
              <div className="table-responsive" style={{ marginBottom: '1.5rem' }}>
                <table className="table table-sm align-middle">
                  <thead>
                    <tr>
                      <th>Opportunity</th>
                      <th>Location</th>
                      <th>Action</th>
                      <th>Client</th>
                      <th>Project</th>
                    </tr>
                  </thead>
                  <tbody>
                    {oppRows.map((row, i) => {
                      const clientProjects = portalProjects.filter(
                        (p) => !row.client_id || String(p.client_id) === String(row.client_id),
                      );
                      return (
                        <tr key={row.ghl_opportunity_id}>
                          <td>
                            <strong>{row.name}</strong>
                            <div className="muted small">{confidenceBadge(row.match_confidence)}</div>
                          </td>
                          <td className="muted small">{row.ghl_location_name}</td>
                          <td>
                            <FormSelect value={row.action} onChange={(e) => updateOpp(i, { action: e.target.value })}>
                              {OPP_ACTION_OPTIONS.map((o) => (
                                <option key={o.value} value={o.value}>{o.label}</option>
                              ))}
                            </FormSelect>
                          </td>
                          <td>
                            <FormSelect
                              value={row.client_id}
                              onChange={(e) => updateOpp(i, { client_id: e.target.value, project_id: '' })}
                            >
                              <option value="">Select client…</option>
                              {portalClients.map((c) => (
                                <option key={c.id} value={c.id}>{clientLabel(c)}</option>
                              ))}
                            </FormSelect>
                          </td>
                          <td>
                            <FormSelect
                              value={row.project_id}
                              disabled={row.action !== 'link'}
                              onChange={(e) => updateOpp(i, { project_id: e.target.value })}
                            >
                              <option value="">Select project…</option>
                              {clientProjects.map((p) => (
                                <option key={p.id} value={p.id}>{p.name}</option>
                              ))}
                            </FormSelect>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </>
          ) : (
            <p className="muted small" style={{ marginBottom: '1.5rem' }}>
              No GHL opportunities found. Set a pipeline on each location first, then refresh preview.
            </p>
          )}

          {!preview.quickbooks_connected ? (
            <p className="muted small">
              QuickBooks is not connected — QB linking will be skipped. Connect in Integrations / Time tracker to
              match customers by name.
            </p>
          ) : null}
        </>
      ) : null}
    </div>
  );
}
